/** Audio is presentation-only. Failure must never affect GameSession. */

export type AudioCategory = 'master' | 'sfx' | 'ui'

/** Future MUSIC slot reserved in schema; unused this batch. */
export type AudioBus = 'sfx' | 'ui' | 'music'

export type SoundId =
  | 'ui.confirm'
  | 'ui.place'
  | 'ui.upgrade'
  | 'ui.sell'
  | 'ui.waveStart'
  | 'tower.fire'
  | 'tower.frost'
  | 'tower.cannon'
  | 'tower.poison'
  | 'tower.aa'
  | 'tower.mage'
  | 'sig.burn'
  | 'sig.deepFreeze'
  | 'sig.shockwave'
  | 'sig.contagion'
  | 'sig.multiLock'
  | 'sig.armorBreak'
  | 'enemy.statusReject'
  | 'enemy.shieldBreak'
  | 'enemy.bossSpawn'
  | 'enemy.bossDeath'
  | 'enemy.leak'
  | 'match.waveClear'
  | 'match.victory'
  | 'match.defeat'

export type SoundPriority =
  | 'critical'
  | 'boss'
  | 'signature'
  | 'wave'
  | 'tower'
  | 'ui'
  | 'minor'

export interface AudioPreferencesV1 {
  version: 1
  muted: boolean
  masterVolume: number
  sfxVolume: number
  uiVolume: number
}

export const AUDIO_STORAGE_KEY = 'shapes-td.audio.v1'

export const DEFAULT_AUDIO_PREFS: AudioPreferencesV1 = {
  version: 1,
  muted: false,
  masterVolume: 0.8,
  sfxVolume: 0.75,
  uiVolume: 0.7
}

export interface VoicePolicy {
  maxVoices: number
  cooldownMs: number
  priority: SoundPriority
  bus: AudioBus
}

export const SOUND_POLICY: Record<SoundId, VoicePolicy> = {
  'ui.confirm': { maxVoices: 2, cooldownMs: 80, priority: 'ui', bus: 'ui' },
  'ui.place': { maxVoices: 2, cooldownMs: 100, priority: 'ui', bus: 'ui' },
  'ui.upgrade': { maxVoices: 2, cooldownMs: 120, priority: 'ui', bus: 'ui' },
  'ui.sell': { maxVoices: 2, cooldownMs: 120, priority: 'ui', bus: 'ui' },
  'ui.waveStart': { maxVoices: 1, cooldownMs: 400, priority: 'wave', bus: 'ui' },
  'tower.fire': { maxVoices: 3, cooldownMs: 55, priority: 'tower', bus: 'sfx' },
  'tower.frost': { maxVoices: 3, cooldownMs: 60, priority: 'tower', bus: 'sfx' },
  'tower.cannon': { maxVoices: 2, cooldownMs: 90, priority: 'tower', bus: 'sfx' },
  'tower.poison': { maxVoices: 3, cooldownMs: 65, priority: 'tower', bus: 'sfx' },
  'tower.aa': { maxVoices: 3, cooldownMs: 50, priority: 'tower', bus: 'sfx' },
  'tower.mage': { maxVoices: 3, cooldownMs: 70, priority: 'tower', bus: 'sfx' },
  'sig.burn': { maxVoices: 2, cooldownMs: 140, priority: 'signature', bus: 'sfx' },
  'sig.deepFreeze': { maxVoices: 2, cooldownMs: 200, priority: 'signature', bus: 'sfx' },
  'sig.shockwave': { maxVoices: 2, cooldownMs: 180, priority: 'signature', bus: 'sfx' },
  'sig.contagion': { maxVoices: 2, cooldownMs: 160, priority: 'signature', bus: 'sfx' },
  'sig.multiLock': { maxVoices: 2, cooldownMs: 120, priority: 'signature', bus: 'sfx' },
  'sig.armorBreak': { maxVoices: 2, cooldownMs: 130, priority: 'signature', bus: 'sfx' },
  'enemy.statusReject': { maxVoices: 2, cooldownMs: 280, priority: 'minor', bus: 'sfx' },
  'enemy.shieldBreak': { maxVoices: 2, cooldownMs: 200, priority: 'boss', bus: 'sfx' },
  'enemy.bossSpawn': { maxVoices: 1, cooldownMs: 800, priority: 'boss', bus: 'sfx' },
  'enemy.bossDeath': { maxVoices: 1, cooldownMs: 800, priority: 'boss', bus: 'sfx' },
  'enemy.leak': { maxVoices: 2, cooldownMs: 250, priority: 'wave', bus: 'sfx' },
  'match.waveClear': { maxVoices: 1, cooldownMs: 500, priority: 'wave', bus: 'sfx' },
  'match.victory': { maxVoices: 1, cooldownMs: 2000, priority: 'critical', bus: 'sfx' },
  'match.defeat': { maxVoices: 1, cooldownMs: 2000, priority: 'critical', bus: 'sfx' }
}

export function priorityRank(p: SoundPriority): number {
  switch (p) {
    case 'critical':
      return 100
    case 'boss':
      return 80
    case 'signature':
      return 65
    case 'wave':
      return 50
    case 'tower':
      return 30
    case 'ui':
      return 25
    default:
      return 10
  }
}

export function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return 0
  return Math.max(0, Math.min(1, v))
}
